import client from './client';

export const getPayrollGenerate = async (params = {}) => {
  const res = await client.get('/payroll/generate', { params });
  return res.data;
};

export const getPayrolls = async (page = 1, limit = 10) => {
  const res = await client.get(
    `/payroll/payrolls?page=${page}&limit=${limit}`
  );
  return res.data;
};

export const searchPayrolls = async (page = 1, limit = 10, searchTerm = '') => {
  const res = await client.get(
    `/payroll/search-payrolls?page=${page}&limit=${limit}&search=${encodeURIComponent(searchTerm)}`
  );
  return res.data;
};

// Get all payroll IDs by store names (for applying allowances across all pages)
export const getAllPayrollIdsByStores = async (storeNames = []) => {
  if (!storeNames || storeNames.length === 0) return [];
  
  try {
    // Fetch all payrolls by making multiple requests if needed
    // Use a high limit and fetch all pages
    const limit = 1000;
    let allPayrollIds = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      // Search for each store name (the backend search uses regex, so we can search for any store)
      // We'll fetch all and filter client-side for exact matches
      const searchTerm = storeNames[0]; // Use first store as search term
      const res = await client.get(
        `/payroll/search-payrolls?page=${page}&limit=${limit}&search=${encodeURIComponent(searchTerm)}`
      );

      console.log("res.data: ",res.data);
      
      if (res.data?.status === 'success' && res.data?.data) {
        // Filter by exact store names and collect IDs
        const pagePayrollIds = res.data.data
          .filter(p => storeNames.includes(p.store))
          .map(p => p._id);
        
        allPayrollIds = [...allPayrollIds, ...pagePayrollIds];
        
        // Check if there are more pages
        const totalPages = res.data.totalPages || 1;
        hasMore = page < totalPages;
        page++;
      } else {
        hasMore = false;
      }
      
      // Safety check to prevent infinite loops
      if (page > 100) {
        console.warn('Reached maximum page limit when fetching payroll IDs');
        break;
      }
    }
    
    // Remove duplicates
    return [...new Set(allPayrollIds)];
  } catch (error) {
    console.error('Error fetching payroll IDs by stores:', error);
    return [];
  }
};

export const searchPayrollsCompleted = async (page = 1, limit = 10, searchTerm = '', signal = null) => {
  const res = await client.get(
    `/payroll/search-payrolls/completed?page=${page}&limit=${limit}&search=${encodeURIComponent(searchTerm)}`,
    { signal }
  );
  return res.data;
};

export const getSavedPayrolls = async (page = 1, limit = 10, store = '', signal = null) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (store) params.set('store', store);
  const res = await client.get(`/payroll/save_payrolls?${params.toString()}`, { signal });
  return res.data;
};

// Simple in-memory cache for payroll details
const payrollDetailCache = new Map();

export const getPayrollById = async (id, options = {}) => {
  if (!id) throw new Error('Missing payroll id');
  const force = options.force === true;
  if (!force && payrollDetailCache.has(id)) return payrollDetailCache.get(id);
  const res = await client.get(`/payroll/${id}/json`);
  // Backend shape: { status, data: { fileName, store, informations, user } }
  const payload = res.data;
  payrollDetailCache.set(id, payload);
  return payload;
};

export const prefetchPayrollById = async (id) => {
  try {
    if (!id || payrollDetailCache.has(id)) return;
    await getPayrollById(id);
  } catch (_) {
    // swallow prefetch errors
  }
};

export const invalidatePayrollCache = (id) => {
  if (!id) {
    payrollDetailCache.clear();
    return;
  }
  payrollDetailCache.delete(id);
};

/** Billing page: hidden allowance column keys (synced with Payroll table column picker). */
export const updatePayslipHiddenAllowanceColumns = async (payrollId, payslip_hidden_allowance_column_keys) => {
  if (!payrollId) throw new Error('Missing payroll id');
  const res = await client.put(`/payroll/${payrollId}/settings/payslip-hidden-allowance-columns`, {
    payslip_hidden_allowance_column_keys
  });
  return res.data;
};

// Update a single payroll info row
export const updatePayrollInfo = async (payrollInfoId, updates) => {
  if (!payrollInfoId) throw new Error('Missing payroll info id');
  const res = await client.put(`/payroll/info/${payrollInfoId}`, updates);
  return res.data;
};

// Delete a payroll by id
export const deletePayroll = async (id) => {
  if (!id) throw new Error('Missing payroll id');
  const res = await client.post('/payroll/delete', { id });
  return res.data;
};

// Save payroll status by id
export const savePayroll = async (id) => {
  if (!id) throw new Error('Missing payroll id');
  const res = await client.post(`/payroll/${id}/save`);
  return res.data;
};

// Allowance operations
export const addAllowance = async (payrollId, empId, allowanceData) => {
  if (!payrollId || !empId) throw new Error('Missing payroll ID or employee ID');
  const res = await client.post(`/payroll/${payrollId}/additional-income`, {
    emp_id: empId,
    ...allowanceData
  });
  return res.data;
};

export const getAllowances = async (payrollId, empId) => {
  if (!payrollId || !empId) throw new Error('Missing payroll ID or employee ID');
  const res = await client.get(`/payroll/${payrollId}/employee/${empId}/allowances`);
  return res.data;
};

// Bulk fetch all allowances for all employees in a payroll
export const getAllAllowancesBulk = async (payrollId) => {
  if (!payrollId) throw new Error('Missing payroll ID');
  const res = await client.get(`/payroll/${payrollId}/allowances/bulk`);
  return res.data;
};

export const deleteAllowance = async (allowanceId) => {
  if (!allowanceId) throw new Error('Missing allowance ID');
  const res = await client.delete(`/payroll/allowance/${allowanceId}`);
  return res.data;
};

// Deduction operations
export const addDeduction = async (payrollId, empId, deductionData) => {
  if (!payrollId || !empId) throw new Error('Missing payroll ID or employee ID');
  const res = await client.post(`/payroll/${payrollId}/additional-deduction`, {
    emp_id: empId,
    ...deductionData
  });
  return res.data;
};

export const getDeductions = async (payrollId, empId) => {
  if (!payrollId || !empId) throw new Error('Missing payroll ID or employee ID');
  const res = await client.get(`/payroll/${payrollId}/employee/${empId}/deductions`);
  return res.data;
};

export const deleteDeduction = async (deductionId) => {
  if (!deductionId) throw new Error('Missing deduction ID');
  const res = await client.delete(`/payroll/deduction/${deductionId}`);
  return res.data;
};

// Legacy methods (keeping for backward compatibility)
export const updateAdditionalIncome = async (payrollId, allowanceData) => {
  if (!payrollId) throw new Error('Missing payroll id');
  const res = await client.post(`/payroll/${payrollId}/additional-income-and-remarks`, allowanceData);
  return res.data;
};

export const updateAdditionalDeduction = async (payrollId, deductionData) => {
  if (!payrollId) throw new Error('Missing payroll id');
  const res = await client.post(`/payroll/${payrollId}/additional-deduction-and-remarks`, deductionData);
  return res.data;
};

// Update government deductions
export const updateGovernmentDeductions = async (payrollInfoId, governmentData) => {
  if (!payrollInfoId) throw new Error('Missing payroll info id');
  const res = await client.post(`/payroll/info/${payrollInfoId}/government`, governmentData);
  return res.data;
};

// Get available payrolls (stores)
export const getAvailablePayrolls = async (page = 1, limit = 100) => {
  const res = await client.get(`/payroll/payrolls?page=${page}&limit=${limit}`);
  return res.data;
};

// Transfer payroll to another store
export const transferPayroll = async (payrollInfoId, transferData) => {
  if (!payrollInfoId) throw new Error('Missing payroll info id');
  const res = await client.post(`/payroll/info/${payrollInfoId}/transfer`, transferData);
  return res.data;
};

// Remove employee from payroll
export const removeEmployeeFromPayroll = async (payrollInfoId) => {
  if (!payrollInfoId) throw new Error('Missing payroll info id');
  const res = await client.post('/payroll/info/remove', { id: payrollInfoId });
  return res.data;
};

// Update payroll cutoff
export const updatePayrollCutoff = async (payrollId, cutoffData) => {
  if (!payrollId) throw new Error('Missing payroll id');
  const res = await client.put(`/payroll/${payrollId}/cutoff`, cutoffData);
  return res.data;
};

// Get holidays within date range
export const getHolidaysInRange = async (startDate, endDate) => {
  if (!startDate || !endDate) throw new Error('Missing startDate or endDate');
  const res = await client.get(`/holidays/range?startDate=${startDate}&endDate=${endDate}`);
  return res.data;
};

// Get payslips data
export const getPayslips = async (payslipsData) => {
  if (!payslipsData.store) throw new Error('Store is required');
  if (!payslipsData.startDate || !payslipsData.endDate) throw new Error('Date period is required');

  const res = await client.post('/payroll/payslips', payslipsData);
  return res.data;
};

// Get stores list for payslips
export const getStores = async () => {
  const res = await client.get('/payroll/stores');
  return res.data;
};

// Get individual employee payslip data
export const getEmployeePayslip = async (payrollId, employeeId) => {
  if (!payrollId || !employeeId) throw new Error('Payroll ID and Employee ID are required');
  const res = await client.get(`/payroll/payslip/${payrollId}/${employeeId}`);
  return res.data;
};

export const getCompletedPayrolls = async () => {
  const res = await client.get('/payroll/completed-payrolls');
  return res.data;
};

// Revert payroll status back to draft
export const revertPayrollToDraft = async (id) => {
  if (!id) throw new Error('Missing payroll id');
  const res = await client.post(`/payroll/${id}/revert-to-draft`);
  return res.data;
};

// OTP-related functions for payroll editing
export const requestEditOtp = async (payrollId) => {
  if (!payrollId) throw new Error('Missing payroll ID');
  const res = await client.post('/payroll/request-edit-otp', { payrollId });
  return res.data;
};

export const verifyOtp = async (otp) => {
  if (!otp) throw new Error('Missing OTP');
  const res = await client.post('/payroll/verify-otp', { otp });
  return res.data;
};

// Verify OTP for 13th month payroll editing
export const verify13thMonthOtp = async (otp) => {
  if (!otp) throw new Error('Missing OTP');
  const res = await client.post('/payroll/13th-month/verify-otp', { otp });
  return res.data;
};

export const changeDisplay = async (id) => {
  if (!id) throw new Error('Missing ID');
  const res = await client.get(`payroll/${id}/display`);
  return res.data;
};

export const updateSource = async (data) => {
  const res = await client.post(`payroll/update/source`, data);
  return res.data;
};

// Create a 13th month payroll record
export const createThirteenthMonthPayroll = async (payload) => {
  if (!payload?.name || !payload?.from || !payload?.to) {
    throw new Error('Missing required fields: store, from, to');
  }
  const res = await client.post('/payroll/13th-month', payload);
  return res.data;
};

export const getThirteenthMonthByUser = async (userId, params = {}) => {
  if (!userId) throw new Error('Missing userId');
  // Optional params: { page, limit, store, from, to, search }
  const res = await client.get(`/payroll/13th-month/user/${userId}`, { params });
  return res.data;
};

// Get summary records for a given thirteenth month payroll record
export const getThirteenthMonthSummary = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.post('/payroll/thirteen-month-summary', { id });
  return res.data;
};

// Saved totals must reflect the employee summaries, not a stale parent total.
export const getThirteenthMonthSummaryTotal = async (id) => {
  const response = await getThirteenthMonthSummary(id);
  if (!Array.isArray(response?.data)) {
    throw new Error('Invalid 13th month summary response');
  }
  return response.data.reduce(
    (total, summary) => total + (Number(summary.total_13th_month) || 0),
    0
  );
};

export const getThirteenthMonthById = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.get(`/payroll/13th-month/${id}`);
  return res.data;
};


export const getAllThirteenthMonthByUser = async (userId, params = {}) => {
  if (!userId) throw new Error('Missing userId');
  // Optional params: { page, limit, store, from, to, search }
  const res = await client.get(`/payroll/13th-month/${userId}/all/json`, { params });
  return res.data;
};


export const getAllThirteenthMonthByUserCSV = async (userId, params = {}) => {
  if (!userId) throw new Error('Missing userId');
  // Optional params: { page, limit, store, from, to, search }
  const res = await client.get(`/payroll/13th-month/${userId}/all/csv`, { params, responseType: 'blob' });
  return res.data;
};

export const getAllThirteenthMonthSummaryCSV = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.get(`/payroll/thirteen-month-summary/${id}/csv`, { responseType: 'blob' });
  return res.data;
};

export const getAllThirteenthMonthSummaryJSON = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.get(`/payroll/thirteen-month-summary/${id}/json`, { id });

  return res.data;
}

export const getCompleteThirteenthMonth = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.post('/payroll/complete/thirteenmonth', { id });
  return res.data;
};

export const removeThirteenthMonth = async (id) => {
  if (!id) throw new Error('Missing thirteenth month record id');
  const res = await client.post('/payroll/thirteenmonth/remove', { id });
  return res.data;
};

// Request OTP for 13th month payroll editing
export const requestEdit13thMonthOtp = async (thirteenthMonthId) => {
  if (!thirteenthMonthId) throw new Error('Missing 13th month payroll ID');
  const res = await client.post('/payroll/13th-month/request-edit-otp', { thirteenthMonthId });
  return res.data;
};

// Revert 13th month payroll to processing status
export const revert13thMonthToProcessing = async (id) => {
  if (!id) throw new Error('Missing 13th month payroll id');
  const res = await client.post(`/payroll/13th-month/${id}/revert-to-processing`);
  return res.data;
};

export const updateThirteenthMonthSummaries = async (thirteenthMonthId, updates) => {
  if (!thirteenthMonthId) throw new Error('Missing thirteenth month record id');
  const res = await client.put(`payroll/thirteen-month-summary/${thirteenthMonthId}/update`, { updates: updates });
  return res.data;
}

export const removeThirteenMonthPersonnel = async (data) => {
  if (!data) throw new Error('Missing 13th month payroll data');
  const res = await client.post(`/payroll/thirteen-month-summary/delete`, data);
  return res.data;
};

export const thirteenMonthPayslip = async (id) => {
  if (!id) throw new Error('Missing 13th month payroll id');
  const res = await client.get(`/payroll/thirteen-month/${id}`);
  return res.data;
};

export const updateThirteenthMonthStatus = async (thirteenthMonthId) => {
  if (!thirteenthMonthId) throw new Error('Missing thirteenth month record id');
  const res = await client.put(`payroll/thirteen-month/${thirteenthMonthId}/update`);
  return res.data;
};

export const updateThirteenthMonthTotal = async (data) => {
  if (!data) throw new Error('Data is required');
  const res = await client.post(`payroll/thirteen-month/update/total`, data);
  return res.data;
};

export const getPayrollAuthorities = async () => {
  const res = await client.get(`payroll-authorities/me`);
  return res.data
}
